import { Form, Input, Select, Switch } from "antd";
import CaretDownIcon from "../../../../../assets/icons/CaretDownIcon";
import CheckboxIcon from "../../../../../assets/icons/CheckboxIcon";
import DeleteIcon from "../../../../../assets/icons/DeleteIcon";
import DescriptionIcon from "../../../../../assets/icons/DescriptionIcon";
import DropdownIcon from "../../../../../assets/icons/DropdownIcon";
import FileUploadIcon from "../../../../../assets/icons/FileUploadIcon";
import MultipleChoiceIcon from "../../../../../assets/icons/MultipleChoiceIcon";
import XIcon from "../../../../../assets/icons/XIcon";
import {
  MAX_FILE_NUMBERS_OPTIONS,
  MAX_FILE_SIZE_OPTIONS,
  QUESTION_CONFIG,
} from "../../constants/ticket.constants";

// Reusable label renderer
const FieldLabel = ({ icon: Icon, text }: { icon: React.FC; text: string }) => (
  <div className="flex items-center gap-2">
    <Icon />
    <span>{text}</span>
  </div>
);

export const REQUIRED_FIELD_TYPES = [
  {
    label: <FieldLabel icon={MultipleChoiceIcon} text="Multiple Choice" />,
    value: "multiple_choice",
  },
  {
    label: <FieldLabel icon={CheckboxIcon} text="Checkboxes" />,
    value: "checkboxes",
  },
  {
    label: <FieldLabel icon={DropdownIcon} text="Dropdown" />,
    value: "dropdown",
  },
  {
    label: <FieldLabel icon={FileUploadIcon} text="File Upload" />,
    value: "file_upload",
  },
  {
    label: <FieldLabel icon={DescriptionIcon} text="Description" />,
    value: "short_answer",
  },
];
export const FIELD_ICON_MAP: Record<string, React.ReactNode> = {
  multiple_choice: <MultipleChoiceIcon />,
  checkboxes: <CheckboxIcon />,
};

const RequiredField = ({
  form,
  field,
  handleRemoveField,
  length = 1,
}: {
  form: any;
  field: any;
  namePrefix: (string | number)[];
  handleRemoveField: Function;
  length?: number;
}) => {
  const selectedType = Form.useWatch(
    ["required_fields", field.name, "required_field_type"],
    form,
  );

  const prefixIcon = FIELD_ICON_MAP?.[selectedType];

  return (
    <div className="mb-6 p-4 shadow rounded-lg bg-white">
      <div className="flex gap-4">
        <Form.Item
          name={[field.name, "question_text"]}
          label={
            QUESTION_CONFIG[selectedType]?.label ??
            QUESTION_CONFIG.default.label
          }
          rules={[
            {
              required: true,
              message:
                QUESTION_CONFIG[selectedType]?.message ??
                QUESTION_CONFIG.default.message,
            },
          ]}
          style={{ flex: 1 }}
        >
          <Input
            size="large"
            placeholder={
              QUESTION_CONFIG[selectedType]?.placeholder ??
              QUESTION_CONFIG.default.placeholder
            }
          />
        </Form.Item>

        <Form.Item
          name={[field.name, "required_field_type"]}
          label="Field Type"
          className="w-48"
          initialValue="multiple_choice"
          rules={[{ required: true, message: "Please select a field type" }]}
        >
          <Select
            size="large"
            placeholder="Select field type"
            suffixIcon={<CaretDownIcon />}
            options={REQUIRED_FIELD_TYPES}
          />
        </Form.Item>
      </div>

      {selectedType === "short_answer" ? null : selectedType ===
        "file_upload" ? (
        <div className="mt-4">
          <table className="w-[70%]">
            <tr>
              <td>Maximum number of files</td>
              <td>
                <Form.Item
                  initialValue={1}
                  name={[field.name, "settings", "max_number_of_files"]}
                >
                  <Select
                    size="large"
                    suffixIcon={<CaretDownIcon />}
                    defaultValue={"1"}
                    options={MAX_FILE_NUMBERS_OPTIONS}
                  />
                </Form.Item>
              </td>
            </tr>
            <tr>
              <td>Maximum file size (MB)</td>
              <td>
                <Form.Item
                  initialValue={10}
                  name={[field.name, "settings", "max_file_size"]}
                >
                  <Select
                    size="large"
                    suffixIcon={<CaretDownIcon />}
                    defaultValue={"10"}
                    options={MAX_FILE_SIZE_OPTIONS}
                  />
                </Form.Item>
              </td>
            </tr>
          </table>
        </div>
      ) : (
        <div className="py-0">
          <Form.List name={[field.name, "options"]} initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <div
                    key={field.key}
                    className="flex items-center justify-between mb-0 border-b border-b-[#D7DFE9]"
                  >
                    <Form.Item
                      name={[index, "required_field_option"]}
                      rules={[
                        {
                          required: true,
                          message: "Please enter option text",
                        },
                      ]}
                      className="w-full"
                    >
                      <Input
                        size="large"
                        prefix={prefixIcon ?? `${index + 1}. `}
                        placeholder={`Option ${index + 1}`}
                        className="border-none focus:shadow-none"
                      />
                    </Form.Item>
                    <div
                      className="px-3 py-1 cursor-pointer"
                      onClick={() => remove(field.name)}
                    >
                      <XIcon />
                    </div>
                  </div>
                ))}

                <div className="text-sm text-[#1B4DFF] cursor-pointer mt-4">
                  <span onClick={() => add("")}>+ Add option or</span>{" "}
                  <span
                    className="text-[#FE7235]"
                    onClick={() => add({ required_field_option: "Other" })}
                  >
                    add “Other”
                  </span>
                </div>

                <div className="flex items-center justify-end gap-4 mt-2"></div>
              </>
            )}
          </Form.List>
        </div>
      )}

      <div className="flex justify-end items-center gap-4 border-t border-t-[#D7DFE9] pt-4 mt-2">
        <button
          disabled={length === 1}
          className="px-4 border-r border-slate-200 cursor-pointer disabled:cursor-auto disabled:opacity-80"
          onClick={() => handleRemoveField()}
          title={
            length === 1
              ? "You must keep at least one question in the template."
              : ""
          }
        >
          <DeleteIcon />
        </button>
        <div className="flex items-center gap-2">
          <label htmlFor="is_required" className="block text-[#3D4A5C] text-sm">
            Required
          </label>
          <Form.Item
            initialValue={false}
            name={[field.name, "is_required"]}
            valuePropName="checked"
            className="!my-0"
          >
            <Switch />
          </Form.Item>
        </div>
      </div>
    </div>
  );
};

export default RequiredField;
